import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getKitchenOrders, setKitchenOrderStatus, cancelKitchenOrder } from '../api/kitchen.api.js';
import CancelOrderSheet from '../components/CancelOrderSheet.jsx';
import { onHubEvent, usingHub } from '../lib/hub.js';
import styles from './KitchenScreen.module.css';

// Cuánto lleva esperando la comanda. En cocina el tiempo es la señal más
// importante: una mesa de 20 minutos es un problema, una de 2 no.
const minutesSince = (iso) => Math.floor((Date.now() - new Date(iso).getTime()) / 60000);

const fmtWait = (min) => (min < 1 ? 'recién' : min < 60 ? `${min} min` : `${Math.floor(min / 60)} h`);

const NEXT = {
  pending:   { status: 'preparing', label: 'Empezar'      },
  preparing: { status: 'ready',     label: 'Marcar lista' },
  ready:     { status: 'served',    label: 'Entregada'    },
};

export default function KitchenScreen() {
  const navigate = useNavigate();
  const [orders,  setOrders]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [busy,    setBusy]    = useState(null);
  const [, forceTick]         = useState(0);
  const first = useRef(true);

  const load = useCallback(async () => {
    try {
      const res = await getKitchenOrders({ status: 'open', limit: 100 });
      setOrders(res.data ?? []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error ?? 'No se pudieron cargar las comandas');
    } finally {
      if (first.current) { setLoading(false); first.current = false; }
    }
  }, []);

  // Con caja central las comandas llegan por WebSocket al instante. Sin caja,
  // sondeo cada 10s: suficiente para el ritmo de un servicio.
  useEffect(() => {
    load();
    const off = onHubEvent('kitchen', (list) => { setOrders(list ?? []); setError(null); if (first.current) { setLoading(false); first.current = false; } });
    const t = setInterval(load, usingHub() ? 60000 : 10000);
    return () => { off(); clearInterval(t); };
  }, [load]);

  // Refresca los minutos de espera sin volver a pedir al servidor
  useEffect(() => {
    const t = setInterval(() => forceTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  const [cancelando, setCancelando] = useState(null);

  // A diferencia de avanzar, cancelar NO es optimista: mueve inventario y
  // queda registrado con nombre. Se espera la confirmación del servidor.
  const cancelar = async (datos) => {
    await cancelKitchenOrder(cancelando._id, datos);
    setOrders((prev) => prev.filter((o) => o._id !== cancelando._id));
  };

  const advance = async (order) => {
    const next = NEXT[order.status];
    if (!next || busy) return;
    setBusy(order._id);
    // Optimista: en cocina el toque debe responder de inmediato
    setOrders((prev) =>
      next.status === 'served'
        ? prev.filter((o) => o._id !== order._id)
        : prev.map((o) => (o._id === order._id ? { ...o, status: next.status } : o))
    );
    try {
      await setKitchenOrderStatus(order._id, next.status);
    } catch {
      load();   // revertir con la verdad del servidor
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <button className="btn btn--ghost" onClick={() => navigate(-1)}>← Volver</button>
        <h1 className={styles.title}>Cocina</h1>
        <span className={styles.count}>
          {orders.length} comanda{orders.length !== 1 ? 's' : ''}
        </span>
      </header>

      {error && <p className={styles.error}>{error}</p>}

      {loading ? (
        <p className={styles.hint}><span className="spinner" /> Cargando…</p>
      ) : orders.length === 0 ? (
        <div className={styles.empty}>
          <p className={styles.emptyTitle}>Todo al día</p>
          <p className={styles.emptyHint}>Las comandas nuevas aparecen aquí solas.</p>
        </div>
      ) : (
        <div className={styles.grid}>
          {orders.map((order) => {
            const wait = minutesSince(order.createdAt);
            const next = NEXT[order.status];
            return (
              <article
                key={order._id}
                className={`${styles.card} ${styles['s_' + order.status]} ${wait >= 15 ? styles.late : ''}`}
              >
                <div className={styles.cardHead}>
                  <div>
                    <p className={styles.tab}>{order.tab_name ?? 'Mostrador'}</p>
                    <p className={styles.folio}>
                      #{order.folio} · {order.station}
                      {/* Solo se muestra a partir del segundo tiempo: marcar
                          "entrada" en un restaurante donde todo es primer
                          tiempo sería ruido en cada comanda. */}
                      {order.course > 1 && (
                        <span className={styles.course}>{order.course_name || `${order.course}º tiempo`}</span>
                      )}
                    </p>
                  </div>
                  <span className={`${styles.wait} ${wait >= 15 ? styles.waitLate : ''}`}>
                    {fmtWait(wait)}
                  </span>
                </div>

                <ul className={styles.items}>
                  {order.items.map((it, idx) => (
                    <li key={idx} className={styles.item}>
                      <span className={styles.qty}>{it.quantity}×</span>
                      <div className={styles.itemBody}>
                        <span className={styles.itemName}>
                          {it.name}
                          {/* De quién es. La cocina emplata por comensal y el
                              mesero sirve sin preguntar de quién era el pescado. */}
                          {it.seats?.length ? (
                            <span className={styles.itemSeat}>
                              {it.seats.length === 1 ? `C${it.seats[0]}` : it.seats.map((n) => `C${n}`).join('·')}
                            </span>
                          ) : null}
                        </span>
                        {it.modifiers?.length > 0 && (
                          <span className={styles.itemMods}>
                            {it.modifiers.map((m) => m.option_name).join(' · ')}
                          </span>
                        )}
                        {it.note && <span className={styles.itemNote}>{it.note}</span>}
                      </div>
                    </li>
                  ))}
                </ul>

                {order.note && <p className={styles.orderNote}>{order.note}</p>}

                <div className={styles.acciones}>
                  {next && (
                    <button
                      className={`btn btn--full ${styles.advance}`}
                      disabled={busy === order._id}
                      onClick={() => advance(order)}
                    >
                      {next.label}
                    </button>
                  )}
                  {/* Discreto a propósito: cancelar es la salida, no el camino */}
                  <button
                    className={styles.cancelar}
                    disabled={busy === order._id}
                    onClick={() => setCancelando(order)}
                  >
                    Cancelar
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <CancelOrderSheet
        open={!!cancelando}
        order={cancelando}
        onCancel={cancelar}
        onClose={() => setCancelando(null)}
      />
    </div>
  );
}
